import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { lessonById } from '../lib/soundLessons';
import { Volume2, Loader2, Lock, Mic, X, CircleAlert } from 'lucide-react';
import { speakFrench, cancelFrench, getSavedTTSVoice, unlockAudioPlayback } from '../lib/tts';
import { playClipSlice, stopClip } from '../lib/clipSlice';
import { UpgradeDialog } from './ProUpgrade';
import { Button } from './ui/button';
import { toast } from 'sonner';
import posthog from 'posthog-js';
import { normWordKey, occOf, occKey, ordinal, countWords, occScoreLookup,
         wordTint, tipCardTint, NEEDS_WORK, feedbackKeys, needsWork, cardScoreText,
         wordScoreDescription, pronunciationBeta } from '../lib/score';

// Shared coaching UI for pronunciation results — one place for word colouring,
// phoneme tips, stress notes and the CEFR badge so Manual / Automatic / the
// session summary all render identical detail without drifting apart.

// Small speaker button next to a flagged word — plays it through the same
// French TTS the rest of the app uses so the learner can hear the target,
// inside its phrase rather than stranded on its own.
export function WordPlayButton({ word }) {
    const [busy, setBusy] = useState(false);
    const play = async (e) => {
        e.stopPropagation();
        if (!word || busy) return;
        unlockAudioPlayback();  // inside the tap, before the TTS fetch
        setBusy(true);
        try {
            cancelFrench();
            // The word on its own, with the language stated, which is what a
            // learner needs to hear. Sending the rest of the sentence as
            // context was tried from 22 to 23 September 2026 and dropped:
            // every word came back around 0.46s, rushed and clipped, as though
            // lifted out of the middle of the phrase. Leanne compared the two
            // side by side and chose the word said alone.
            await speakFrench(word, 1.0, getSavedTTSVoice(), null, true);
        } catch { /* silent — TTS is a nicety here, not critical */ }
        finally { setBusy(false); }
    };
    return (
        <button
            type="button"
            onClick={play}
            aria-label={`Listen to ${word}`}
            title={`Listen to ${word}`}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-primary hover:bg-primary/10 disabled:opacity-50"
            disabled={busy}
        >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Volume2 className="h-3.5 w-3.5" />}
        </button>
    );
}

// The other half of the comparison: the same word, in their own voice, cut out
// of the recording they have just made.
//
// This replaces a solo drill that recorded and scored the word on its own.
// That scoring was never trustworthy: a word said alone is an easier test than
// the same word inside a sentence, so it produced a second number that could
// not be compared with anything, and people practised the number rather than
// the sentence. Hearing the difference is the thing that actually teaches the
// sound, so that is all this does. Practise the word as many times as you like
// out loud; nothing here is recorded, scored or counted.
export function WordVoiceButton({ word, audio, start, end }) {
    const [playing, setPlaying] = useState(false);
    if (!audio) return null;
    const play = async (e) => {
        e.stopPropagation();
        if (playing) { stopClip(); setPlaying(false); return; }
        setPlaying(true);
        try {
            cancelFrench();       // never both voices at once
            await playClipSlice(audio, start, end, (info) => posthog.capture('own_voice_slice', {
                word, start: start ?? null, end: end ?? null, ...info,
                audio_kind: typeof audio === 'string' ? 'url' : 'blob',
                audio_type: typeof audio === 'string' ? null : audio.type,
                audio_bytes: typeof audio === 'string' ? null : audio.size,
            }));
        } catch (err) {
            // Not silent. A model word that will not play is a nicety missed;
            // their own voice not playing looks like the feature is broken, and
            // swallowing the reason cost an afternoon of guessing at it.
            console.error('[Mimique] own-voice playback failed', { word, start, end, err });
            toast.error('Could not play that back.');
        }
        finally { setPlaying(false); }
    };
    return (
        <button
            type="button"
            onClick={play}
            aria-label={`Hear how you said ${word}`}
            title={`Hear how you said "${word}"`}
            className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                playing ? 'bg-accent text-accent-foreground' : 'text-accent hover:bg-accent/10'}`}
        >
            <Mic className="h-3.5 w-3.5" />
        </button>
    );
}

// How many word-cards the tips list shows lives in lib/score
// (MAX_FEEDBACK_WORDS), shared with the highlighting. Every flagged word is
// still reachable by tapping it in the phrase itself.

// Sound lines shown for ONE word. A word that scored 0 fails on all of them.
const MAX_PHONE_LINES = 2;

// Written coaching lines shown for ONE attempt at a word. The backend sends one
// per attempt; the cap catches older results, where three notes on a single
// "travaille" could otherwise stack up on one card.
const MAX_ISSUE_LINES = 1;

// Word matching and "which time it comes up" live in lib/score.js, shared with
// the phrase display, so the colours on the phrase and the cards underneath
// can never disagree about how many attempts a repeated word had.

// The "You said" half of a sound line. When what they said matches the target
// it was the right sound, just not clear enough to score well: the scorer's
// nearest match is the sound itself. Repeating the target in red read as
// "you got it wrong" when they had not, so that case shows only "Aim for" and
// leaves the written tip underneath to say what to change.
const soundKey = (s) => (s || '').replace(/[\s/[\]]/g, '').toLowerCase();
const sameSound = (target, heard) => !!soundKey(heard) && soundKey(heard) === soundKey(target);
function YouSaid({ target, heard, heardParts = [] }) {
    // Beta accounts: on EVERY card, on its own line under "Aim for" (Leanne,
    // 18 Oct 2026: never only sometimes), the scorer's guess as it came.
    // Each sound in the colour of its score, the same bands as the syllables
    // (Leanne, 27 Oct 2026). Results from before the parts were sent: green
    // when it matches (the right sound, said weakly), red when it differs.
    if (pronunciationBeta()) {
        const said = heard || target;
        if (!said) return null;
        return (
            <span className="block">
                <span className="font-semibold">You said:</span>{' '}
                {heardParts.length
                    ? heardParts.map((p, i) => (
                        <span key={i}>
                            {i > 0 && '-'}
                            <span className={`${cardScoreText(p.s)} ${needsWork(p.s) ? 'font-bold' : ''}`}>{p.t}</span>
                        </span>
                    ))
                    : <span className={sameSound(target, said) ? 'text-green-700' : 'text-rose-600'}>{said}</span>}
            </span>
        );
    }
    if (!heard || sameSound(target, heard)) return null;
    return <>{' · '}You said: <span className="font-medium text-rose-600">{heard}</span></>;
}

// The sound tips for ONE word. Lives here so the tips card and the word picked
// out of a phrase say the same thing in the same words — two copies of this
// would drift the first time either was touched.
// The target word spelt out sound by sound. Plain, in the green it sits in:
// it is the right version. The colours by score belong on "You said", where
// the weak sounds actually are (Leanne, 27 Oct 2026; on Aim for from 18 Oct).
function SpelledWord({ spelling }) {
    return spelling.expected;
}

// "Syllables: pré · pa · rer", the weak ones tinted like a weak word in the
// phrase. One copy for every card that lists them.
function SyllableList({ syllables = [] }) {
    return (
        <>
            <span className="font-semibold">Syllables:</span>{' '}
            {syllables.map((s, j) => (
                <span key={j}>
                    {j > 0 && <span className="text-stone-400"> · </span>}
                    <span className={needsWork(s.score) ? `px-0.5 rounded font-medium ${wordTint(s.score)}` : ''}>
                        {s.letters}
                    </span>
                </span>
            ))}
        </>
    );
}

function WordTipLines({ phone = [], stress = [], issues = [], className = 'mt-1', hideSyllables = false,
                       showLesson = false,
                       // Whether ANY word on this page got a written explanation.
                       // The choice between real coaching and raw phoneme labels
                       // has to be made once for the whole clip: made per word, a
                       // card saying "keep your tongue mid-mouth" sat directly
                       // above one saying "the e in bed", and the page read as
                       // though two different apps had written it.
                       coached = false }) {
    if (!phone.length && !stress.length && !issues.length) return null;
    // A coached page where this particular word was not covered still shows
    // its own sounds, so every word has something to aim for.
    const showPhone = !coached || !issues.length;
    // The word spelt right through. Every tip for a word carries the same
    // spelling, so the first one that has it speaks for the card. Absent on
    // results scored before this existed, which fall back to naming the sound.
    const spelt = phone.find(t => t.word_expected);
    const wordSpelling = spelt ? { expected: spelt.word_expected, heard: spelt.word_heard,
                                   heardParts: spelt.word_heard_parts || [] } : null;
    // A longer word's syllables, the weak ones tinted like a weak word in the
    // phrase, so the part to work on stands out: pré · pa · rer.
    const syllables = (phone.find(t => (t.syllables || []).length > 1) || {}).syllables || [];
    const syllableLine = !hideSyllables && syllables.length > 1 && (
        <li key="syll" className="text-sm text-stone-600"><SyllableList syllables={syllables} /></li>
    );

    // Beta layout (Leanne, 7 Oct 2026): the syllables first, then what to work
    // on as one line or, when there is more than one, a short bulleted list
    // under one heading, and "Aim for" with "You said" at the end.
    if (pronunciationBeta() && showPhone && wordSpelling) {
        const seen = new Set();
        const items = [...phone]
            .sort((a, b) => (a.score ?? 100) - (b.score ?? 100))
            .slice(0, MAX_PHONE_LINES)
            .map(t => (t.kind
                // A silent letter or a missed link: the hint says it all.
                ? { key: t.hint, label: '', hint: t.hint }
                : { key: t.expected_label, label: t.expected_label, hint: t.hint || '' }))
            .filter(it => (it.label || it.hint) && !seen.has(it.key) && seen.add(it.key));
        const itemText = (it) => (it.label
            ? <>{it.label}{it.hint ? `. ${it.hint}` : ''}</>
            : it.hint);
        return (
            <ul className={`space-y-0 leading-snug ${className}`}>
                {syllableLine}
                {items.length === 1 && (
                    <li key="sound" className="text-sm text-stone-600">
                        {items[0].label
                            ? <><span className="font-semibold">Sound to work on:</span> {itemText(items[0])}</>
                            : itemText(items[0])}
                    </li>
                )}
                {items.length > 1 && (
                    <li key="sounds" className="text-sm text-stone-600">
                        <span className="font-semibold">Sounds to work on:</span>
                        <ul className="list-disc pl-4">
                            {items.map(it => <li key={it.key}>{itemText(it)}</li>)}
                        </ul>
                    </li>
                )}
                {stress.map((t, j) => (
                    <li key={`s${j}`} className="text-sm text-stone-600">
                        Stress {t.syllable ? <>(syllable <span className="font-mono">{t.syllable}</span>) </> : null}
                        needs to be {t.expected > (t.actual ?? 0) ? 'a bit more strongly' : 'a bit more lightly'}.
                    </li>
                ))}
                {/* A lesson for the sound, when there is one (Leanne, 25 Oct 2026).
                    The session summary only: mid-session it pulled them off the
                    practice (Leanne, 27 Oct 2026). */}
                {showLesson && (() => {
                    const lesson = phone.map(t => lessonById(t.lesson)).find(Boolean);
                    return lesson && (
                        <li key="lesson" className="text-sm text-stone-600">
                            <Link to={`/sounds/${lesson.id}`} className="font-semibold text-primary underline underline-offset-2"
                                  data-testid="sound-lesson-link" onClick={(e) => e.stopPropagation()}>
                                Practise: {lesson.name}
                            </Link>
                        </li>
                    );
                })()}
                {/* Padding, not a margin: the list's space-y-0 sets a zero top margin
                    on every item after the first with higher priority than any
                    mt- class, so the gap asked for on 7 Oct never showed. */}
                <li key="word" className="text-sm text-stone-600 pt-2">
                    <span className="font-semibold">Aim for:</span> <span className="text-green-700"><SpelledWord spelling={wordSpelling} /></span>
                    {/* No "You said" when a sound scored 0 with no guess and the
                        word came back exactly as the target: the scorer could not
                        place that sound, and "You said EE-L" under a 0 read as
                        though they had said it right (Leanne, 23 Oct 2026). */}
                    {!(phone.some(t => !t.kind && (t.score ?? 100) === 0 && !t.heard)
                       && sameSound(wordSpelling.expected, wordSpelling.heard || wordSpelling.expected)) && (
                        <YouSaid target={wordSpelling.expected} heard={wordSpelling.heard}
                                 heardParts={wordSpelling.heardParts} />
                    )}
                </li>
            </ul>
        );
    }

    return (
        <ul className={`space-y-0 leading-snug ${className}`}>
            {/* Gemini translates Speechace's raw phone-code swap into a real
                explanation (tongue/lip position, a concrete fix) — show that
                instead of the bare "/ʁ/ came out closer to /r/" line. Only fall
                back to the raw swap if Gemini didn't cover this word this time. */}
            {/* Worst first, and only the worst two. A word that scored badly
                fails on every sound in it, and eight lines of English
                comparisons about one French word is not coaching, it is a
                wall. The word itself is highlighted and can be heard both
                ways, which says more than the ninth line would. */}
            {/* The word spelt right through, target against what came back:
                "Aim for D-UH-M-AEN, you said M-AEN-M-AEN". The same shape the
                coaching writes, so a card reads the same whichever wrote it,
                and something a learner can say straight back, which a single
                sound named on its own never was. */}
            {showPhone && wordSpelling && (
                <li key="word" className="text-sm text-stone-600">
                    Aim for: <span className="font-medium text-green-700"><SpelledWord spelling={wordSpelling} /></span>
                    <YouSaid target={wordSpelling.expected} heard={wordSpelling.heard} />
                </li>
            )}
            {syllableLine}
            {/* How to actually make the sounds that went wrong, worst first and
                only the worst two: a wall of them is not coaching. Older
                results, from before the word was spelt out, still show the one
                sound named on its own. */}
            {showPhone && [...phone]
                .sort((a, b) => (a.score ?? 100) - (b.score ?? 100))
                .slice(0, MAX_PHONE_LINES)
                .map((t, j) => (
                // Every card names the sound to work on, so a card never
                // says "one or two clear mistakes" without saying which. A
                // silent letter or a missed link has a hint that already says
                // exactly what to do, and shows that alone.
                (pronunciationBeta() && wordSpelling && !t.kind && t.expected_label) ? (
                <li key={`p${j}`} className="text-sm text-stone-600">
                    Sound to work on: <span className="font-medium">{t.expected_label}</span>
                    {t.hint ? `. ${t.hint}` : ''}
                </li>
                ) : (t.hint || (!wordSpelling && t.expected_label)) ? (
                <li key={`p${j}`} className="text-sm text-stone-600">
                    {t.hint || (!t.expected_label && 'One sound in this word needs work.')}
                    {!wordSpelling && t.expected_label && (
                        <span className={`block text-sm text-stone-500 ${t.hint ? 'mt-0.5' : ''}`}>
                            Aim for: <span className="font-medium text-green-700">{t.expected_label}</span>
                            <YouSaid target={t.expected_label} heard={t.heard_label} />
                        </span>
                    )}
                </li>
                ) : null
            ))}
            {stress.map((t, j) => (
                <li key={`s${j}`} className="text-sm text-stone-600">
                    Stress {t.syllable ? <>(syllable <span className="font-mono">{t.syllable}</span>) </> : null}
                    needs to be {t.expected > (t.actual ?? 0) ? 'a bit more strongly' : 'a bit more lightly'}.
                </li>
            ))}
            {issues.slice(0, MAX_ISSUE_LINES).map((issue, j) => (
                <li key={`g${j}`} className="text-sm text-stone-600">
                    {issue.tip}
                    {issue.expected_ipa && issue.perceived_ipa && (
                        // Labelled, not "a → b": a bare arrow never said which
                        // side was the target and which was them.
                        <span className="block text-sm text-stone-500 mt-0.5">
                            Aim for: <span className="font-medium text-green-700">{issue.expected_ipa}</span>
                            <YouSaid target={issue.expected_ipa} heard={issue.perceived_ipa} />
                        </span>
                    )}
                </li>
            ))}
        </ul>
    );
}

// Only words that need attention get highlighted — good words stay plain so
// the eye lands on the problem areas. Palette matches the rest of the app
// (amber for warning, rose for poor).
// What a highlighted word says when nothing inside it was singled out. One
// copy, so the tip card and the tapped-word bar cannot word it differently.
const NO_SOUND_LINE = 'No single sound stood out here, it is the word as a whole that needs work. Listen to it, then say it again.';

// A tapped word with no tip of its own still gets a card: its name, a way to
// hear it, its score and what that score means (Leanne, 16 Oct 2026). One
// component for Manual and the hands-free score view, so they cannot drift.
export function WordCard({ word, label = '', score = null, note = '', dense = false, syllables = [],
                         plain = false }) {
    const rounded = score == null ? null : Math.round(score);
    const meaning = rounded == null ? '' : wordScoreDescription(rounded, 0);
    return (
        <div className={`${dense ? 'text-xs px-3 py-2' : 'text-sm px-3 py-2.5'} rounded-lg border text-left text-stone-700 ${tipCardTint(plain ? null : rounded)}`}
             data-testid="word-card">
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5">
                    <CardWord>{label || word}</CardWord>
                    <WordPlayButton word={word} />
                </div>
                {rounded != null && (
                    <span className={`text-sm font-bold ${cardScoreText(rounded)} tabular-nums`}>{rounded}%</span>
                )}
            </div>
            {syllables.length > 1 && (
                <p className="text-sm leading-snug text-stone-600"><SyllableList syllables={syllables} /></p>
            )}
            {meaning && <p className="text-sm leading-snug text-stone-500">{meaning}</p>}
            {note && <p className="mt-1 text-sm leading-snug text-stone-600">{note}</p>}
        </div>
    );
}

// The syllables the server sent for one attempt at a word, if it has more than one.
const syllablesFor = (wordScores, word, occ) =>
    ((wordScores || []).find(w => occKey(w.word, occOf(w)) === occKey(word, occ)) || {}).syllables || [];

// What the card says when nothing was singled out in the word: the same two
// lines as before, chosen by whether the word passed.
const noTipNote = (score) => (needsWork(score)
    ? NO_SOUND_LINE
    : 'No sound tip for this one, it scored well enough to pass.');



/**
 * Renders the target phrase with each word tinted by its Speechace
 * quality_score. Falls back to plain rendering when no scores are available
 * (e.g. Speechace didn't run, or the phrase's tokenisation didn't match).
 *
 * With `wordPickable`, every word becomes tappable: pick one and you can hear
 * it said properly, then hear yourself saying it, cut straight out of the
 * recording you have just made (`recordedAudio`, the Blob or a url for it).
 *
 * Deliberately EVERY word, not only the flagged ones. The tips card can only
 * offer what the scorer noticed, and the scorer does not notice everything —
 * /u/ against /y/ ("beaucoup" against "beau cul") is exactly the pair a learner
 * knows they are getting wrong while the score sails past it. Waiting to be
 * told which word to practise is no good when you already know.
 *
 * Paid only, and not shown to Free at all: no tappable words, no prompt to tap
 * them. Free is scored by Gemini and has no acoustic scoring behind it, so
 * there is nothing here it could honestly do.
 */
export function ScoredPhrase({ phrase, wordScores, className = '', wordPickable = false, scoreLocked = false,
                              phoneTips = [], stressTips = [], issues = [], footerTarget = null,
                              recordedAudio = null, onPick = null,
                              missedWords = [], missedAtEnd = false, missedAdvice = 'stop', missedAtStart = false,
                              missedDetail = [] }) {
    const [picked, setPicked] = useState(null);
    // A new result (Try again, next phrase) clears the tapped word: the old
    // word's tip stayed on screen over the new attempt. Keyed on the words and
    // scores rather than the array, which can be a fresh [] every render.
    const resultKey = `${phrase}|${(wordScores || []).map(w => `${w.word}:${w.score}`).join(',')}`;
    useEffect(() => { setPicked(null); }, [resultKey]);
    // Gated on this result having no score, not on the plan. Free is scored
    // in full during its allowance, so a free result is as pickable as any.
    const canPick = wordPickable && !scoreLocked;
    // Whatever the scorer said about the word they picked, in the same words
    // the tips card uses. Often nothing: the scorer only comments on what it
    // noticed, and picking a word it was happy with is a perfectly ordinary
    // thing to do.
    // `picked` is { word, occ }: which word, and which time it comes up, so
    // tapping the second "le" shows the second attempt's tip, not the first's.
    const pickedKey = picked ? occKey(picked.word, picked.occ) : null;
    const isPicked = (t) => occKey(t.word, occOf(t)) === pickedKey;
    const pickedTips = pickedKey ? {
        phone: phoneTips.filter(isPicked),
        stress: stressTips.filter(isPicked),
        issues: issues.filter(isPicked),
    } : null;
    // A page that has coaching only counts a word as having a tip when the
    // coaching covers it. Otherwise the word gets the same plain line as any
    // other uncovered word, rather than a different kind of tip.
    const anyCoaching = (issues || []).length > 0;
    // Anything at all to say about this word. What it says is WordTipLines'
    // decision, so the bar under the phrase and the card at the bottom cannot
    // word the same situation two different ways.
    const hasTip = !!pickedTips && (pickedTips.phone.length || pickedTips.stress.length
                                    || pickedTips.issues.length);
    if (!phrase) return null;
    // Each attempt at a word coloured by its own score. It used to be the
    // lowest of the two for a repeated word, which painted a well-said "le" red
    // because of the other one.
    const scores = occScoreLookup(wordScores);
    // Only a word with a tip card is tinted: a colour with nothing underneath
    // to explain it is a puzzle, whatever the word scored.
    const tipKeys = feedbackKeys({ phoneTips, stressTips, issues, wordScores,
                                   missed: pronunciationBeta() ? missedDetail : [] });
    const pickedMissed = !!picked && (missedDetail || []).some(m => occKey(m.word, occOf(m)) === pickedKey);
    const repeats = countWords(phrase);
    const tokens = phrase.split(/(\s+)/);
    // The picked word's own score, so the "no tip" line can tell a word that
    // passed from a word that failed without a single sound to blame.
    const pickedScore = picked ? scores.get(picked.word, picked.occ) : undefined;
    const pickedLabel = picked
        ? (repeats.get(normWordKey(picked.word)) > 1 ? `${picked.word} (${ordinal(picked.occ)})` : picked.word)
        : '';

    const nextOccurrence = scores.counter();
    const body = tokens.map((tok, i) => {
        if (!tok.trim()) return <span key={i}>{tok}</span>;
        // Strip trailing punctuation so 'chiens,' still matches 'chiens'.
        const clean = tok.replace(/[^\p{L}\p{M}'\-]/gu, '');
        const occ = nextOccurrence(clean);
        const score = occ && tipKeys.has(occKey(clean, occ)) ? scores.get(clean, occ) : undefined;
        const bg = wordTint(score);
        // Words that scored well (or that Speechace didn't score) stay
        // plain — only the ones needing attention get highlighted.
        if (!canPick || !clean) {
            if (!bg) return <span key={i}>{tok}</span>;
            return <span key={i} className={`px-1 rounded ${bg}`}>{tok}</span>;
        }
        const on = !!picked && picked.word === clean && picked.occ === occ;
        return (
            <button
                key={i}
                type="button"
                onClick={() => { const next = on ? null : { word: clean, occ }; setPicked(next); onPick?.(next); }}
                aria-label={`Practise ${clean} on its own`}
                className={`px-1 rounded transition-colors ${bg || ''} ${
                    on ? 'relative z-10 ring-2 ring-primary ring-offset-1' : 'hover:bg-primary/10'}`}
            >
                {tok}
            </button>
        );
    });

    // The controls and the tip for the picked word. Rendered through a portal
    // into `footerTarget` when the caller gives one, so it lands BELOW both
    // phrases instead of inside the panel holding this one.
    //
    // Measured on a 375px phone: with it inside the panel, picking a word
    // pushed the "Your voice" phrase down 78px. The target phrase itself never
    // moved (a ring is a box-shadow), but on a narrow screen the panels stack,
    // so growing one shunts the other down the page. Growth below everything
    // is ordinary; growth between two phrases reads as the page jumping.
    const pickUI = canPick ? (
        <div data-testid="word-pick-footer">
            {/* Fixed height, held whether a word is picked or not, so swapping
                the prompt for the bar does not move what follows either. */}
            <div className={pronunciationBeta() ? '' : 'h-9'}>
                {pronunciationBeta() ? null : picked ? (
                    // One bar rather than controls scattered through the phrase:
                    // the phrase stays readable and the buttons stay thumb-sized.
                    <div className="flex h-full items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2"
                         data-testid="word-pick-bar">
                        <span className="text-sm font-semibold text-foreground truncate">{pickedLabel}</span>
                        {/* Their own voice for the word (WordVoiceButton) is
                            off until a way of cutting it out reliably is found:
                            the scorer's timings miss on the badly said words,
                            which are the ones being practised. See
                            MIMIQUE-STATUS.md, "PARKED: hear the word in your
                            own voice". */}
                        <WordPlayButton word={picked.word} />
                        <button type="button" onClick={() => { setPicked(null); onPick?.(null); }} aria-label="Close"
                                className="ml-auto inline-flex items-center justify-center w-6 h-6 rounded-full text-stone-400 hover:bg-stone-100">
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                ) : (
                    <p className="flex h-full items-center text-sm text-stone-500">
                        Tap any word in the phrase above to hear it.
                    </p>
                )}
            </div>
            {/* What the scorer said about that word, under the controls for it,
                rather than making them hunt for it in the list further down.
                Same component as that list, so the wording cannot drift.

                Silence would be ambiguous: no tip and "we did not look" read
                identically, so a word it was happy with says so. */}
            {picked && pickedMissed && pronunciationBeta() && (
                <div className="mt-2">
                    <WordCard word={picked.word} label={pickedLabel}
                              syllables={syllablesFor(wordScores, picked.word, picked.occ)}
                              note={missedWordLine(picked.word, missedAtEnd, missedAdvice,
                                                   missedAtStart && isFirstMissed(missedDetail, picked))} />
                </div>
            )}
            {picked && !(pickedMissed && pronunciationBeta()) && (
                hasTip
                    // Beta: the word's full tip card, the same card the list
                    // used to show, so tapping a word is the one way to its
                    // tips (Leanne, 7 Oct 2026).
                    ? (pronunciationBeta()
                        ? <div className="mt-2">
                              <PronunciationTips phoneTips={pickedTips.phone} stressTips={pickedTips.stress}
                                                 issues={pickedTips.issues} title="" phrase={phrase}
                                                 wordScores={wordScores} />
                          </div>
                        : <WordTipLines phone={pickedTips.phone} stress={pickedTips.stress}
                                        issues={pickedTips.issues} className="mt-2"
                                        coached={anyCoaching} />)
                    // Never claim a word passed when it is sitting there
                    // highlighted. A word can score badly as a whole without
                    // any single sound in it dropping below the tip threshold,
                    // and telling somebody a red word "scored well enough"
                    // contradicts the colour they are looking at.
                    // Nothing was flagged on this word at all, which is a
                    // different thing from "flagged but not explained" and is
                    // the one case WordTipLines has nothing to render for.
                    : (pronunciationBeta()
                        ? <div className="mt-2">
                              <WordCard word={picked.word} label={pickedLabel} score={pickedScore} plain
                                        syllables={syllablesFor(wordScores, picked.word, picked.occ)}
                                        note={noTipNote(pickedScore)} />
                          </div>
                        : <p className="mt-2 text-sm text-stone-500">{noTipNote(pickedScore)}</p>)
            )}
        </div>
    ) : null;

    return (
        <div className={className}>
            <p className="text-sm text-foreground font-medium leading-relaxed" data-testid="scored-phrase">
                {body}
            </p>
            {/* No footer target given: fall back to rendering in place, which is
                still correct, just not shift-free on a narrow screen. */}
            {pickUI && (footerTarget ? createPortal(pickUI, footerTarget) : <div className="mt-2">{pickUI}</div>)}
        </div>
    );
}
/**
 * Coaching tips list. Merges Speechace's `phoneTips`/`stressTips` (real
 * acoustic scores) with Gemini's `issues` into one card per word, ordered to
 * match `phrase` (the target phrase text) so tips read left-to-right instead
 * of Speechace's grouped first and Gemini's tacked on the end. `phrase` is
 * optional — omit it (e.g. the session summary, which spans several phrases)
 * to keep first-seen order instead. `title` lets callers change the heading.
 */
// How many sounds a word's card lists under "Sound(s) to work on": distinct,
// and no more than the card shows, so the line above it says the same number.
const cardSoundCount = (phone = []) =>
    Math.min(new Set(phone.map(t => (t.kind ? t.hint : t.expected_label)).filter(Boolean)).size, MAX_PHONE_LINES);

// Whether a tapped missed word is the first one missed: with missedAtStart,
// that is the word the recording began after.
const isFirstMissed = (missed, picked) => {
    const first = (missed || [])[0];
    return !!first && occKey(first.word, occOf(first)) === occKey(picked.word, picked.occ);
};

// What a tapped word that was never said shows instead of a card.
// Words the scorer could not find in the recording. At the end of the phrase
// the recording stopped before they were said: in hands-free mode the fix is a
// slower speed (the recording window follows the speed), in Manual it is to
// keep recording to the last word. Mid-phrase the scorer may only have lost a
// word it could not line up, so that is "not picked up", never "left out".
export const missedWordLine = (word, atEnd = false, advice = 'stop', atStart = false) => {
    // The recording began after they started speaking (18 Oct 2026: in
    // hands-free mode the first word was often lost this way).
    if (atStart) return `"${word}" was not heard. The recording started after you began. ` + (advice === 'speed'
        ? 'Wait until "Speak now" shows before you start.'
        : 'Start speaking just after you tap record.');
    if (!atEnd) return `"${word}" was not picked up. Say every word, even the ones you are unsure of.`;
    return `"${word}" was not heard. The recording stopped before you finished. ` + (advice === 'speed'
        ? "If you can't keep up, lower the speed to give yourself more time, and keep going without long pauses."
        : 'Keep recording until you have said the last word.');
};

// The line under the Feedback box, above the phrase, in Manual and Automatic
// (Leanne, 21 Oct 2026): score, Feedback box, this line, then the phrase.
// Boxed with an amber tip icon, the amber used for warnings across the app:
// as a plain grey line it got lost (Leanne, 27 Oct 2026).
export function TapWordPrompt({ className = 'mb-4' }) {
    return (
        <div className={`w-full max-w-sm mx-auto flex items-center justify-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-2 ${className}`}
             data-testid="tap-word-prompt">
            {/* Left aligned and small enough to stay on one line on a phone
                (Leanne, 4 Nov 2026). */}
            <CircleAlert className="h-4 w-4 flex-shrink-0 text-amber-500" />
            <p className="text-xs text-stone-700 whitespace-nowrap">Tap highlighted words for tips.</p>
        </div>
    );
}

// The word at the top of its card. Larger than the card text, which was made
// bigger and left the word itself looking lost (Leanne, 27 Oct 2026).
function CardWord({ children }) {
    return <span className="text-lg font-bold text-stone-900 leading-tight">{children}</span>;
}

// The tips under a tappable phrase: the card for the word they tapped, or the
// prompt to tap one. Shared by every screen where a highlighted word can be
// tapped, so the wording and the card cannot drift.
export function PickedWordTips({ phoneTips = [], stressTips = [], issues = [], picked = null,
                                 phrase = '', wordScores = [], dense = false, missed = [],
                                 missedAtEnd = false, missedAdvice = 'speed', missedAtStart = false }) {
    if (!picked) return null;
    const key = occKey(picked.word, picked.occ);
    if ((missed || []).some(m => occKey(m.word, occOf(m)) === key)) {
        return <WordCard word={picked.word} dense={dense}
                         syllables={syllablesFor(wordScores, picked.word, picked.occ)}
                         note={missedWordLine(picked.word, missedAtEnd, missedAdvice,
                                              missedAtStart && isFirstMissed(missed, picked))} />;
    }
    const mine = (t) => occKey(t.word, occOf(t)) === key;
    const tips = { phone: phoneTips.filter(mine), stress: stressTips.filter(mine), issues: issues.filter(mine) };
    if (!tips.phone.length && !tips.stress.length && !tips.issues.length) {
        const own = occScoreLookup(wordScores).get(picked.word, picked.occ);
        return <WordCard word={picked.word} dense={dense} score={own} note={noTipNote(own)} plain
                         syllables={syllablesFor(wordScores, picked.word, picked.occ)} />;
    }
    return (
        <PronunciationTips phoneTips={tips.phone} stressTips={tips.stress} issues={tips.issues}
                           title="" phrase={phrase} wordScores={wordScores} dense={dense} />
    );
}

export function PronunciationTips({ phoneTips = [], stressTips = [], issues = [], title = 'Tips to improve',
                                   dense = false, scoreLocked = false, phrase = '',
                                   // Their recording and where each word sits in it, so a
                                   // flagged word can be heard in their own voice here too,
                                   // not only on the word picked out of the phrase above.
                                   recordedAudio = null, wordScores = [],
                                   // The session summary: worst word first, and the
                                   // words that scored well left out when there are
                                   // weaker ones to look at (Leanne, 18 Oct 2026).
                                   worstFirst = false, hideGood = false,
                                   // A "Learn this sound" link on each card: the
                                   // session summary only, never mid-session.
                                   showLessons = false }) {
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    // Where each word sits in their recording, keyed the same way words are
    // matched everywhere else in this file.
    const spans = new Map();
    // Keyed by word AND which time it comes up, the same as the cards below.
    const wordScoreByKey = new Map();
    for (const w of wordScores || []) {
        if (!normWordKey(w.word)) continue;
        const key = occKey(w.word, occOf(w));
        if (w.start != null && !spans.has(key)) spans.set(key, { start: w.start, end: w.end });
        if (!wordScoreByKey.has(key) || wordScoreByKey.get(key) > w.score) {
            wordScoreByKey.set(key, w.score);
        }
    }
    // Did Gemini write anything for THIS clip. One answer for the whole page,
    // so every card is in the same voice.
    const coached = (issues || []).length > 0;
    const hasSpeechace = (phoneTips && phoneTips.length > 0) || (stressTips && stressTips.length > 0);
    const hasFallback = issues && issues.length > 0;
    // A highlighted word owes a card even when nothing was said about it.
    const hasHighlighted = (wordScores || []).some(w => w.score != null && w.score < NEEDS_WORK);
    if (!hasSpeechace && !hasFallback && (scoreLocked || !hasHighlighted)) {
        // Free never gets a tip list at all (just the score + feedback
        // sentence), so show a locked, greyed-out preview instead of nothing
        // — enough to see there's more, not enough to read. A paid user with
        // nothing this call (quota/Speechace hiccup) still gets nothing, as
        // before.
        if (!scoreLocked) return null;
        return (
            <div className="text-left" data-testid="pronunciation-tips-locked">
                {title && <p className={`font-semibold text-stone-700 mb-2 ${dense ? 'text-xs' : 'text-sm'}`}>{title}</p>}
                <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50/60 p-3">
                    <div aria-hidden="true" className="space-y-2 opacity-50 blur-[1.5px] pointer-events-none select-none">
                        <div className="h-2.5 w-3/4 rounded bg-stone-300" />
                        <div className="h-2.5 w-1/2 rounded bg-stone-300" />
                        <div className="h-2.5 w-2/3 rounded bg-stone-300" />
                    </div>
                    {/* Same green Unlock button as every other lock in the app.
                        The amber pill it replaced said its own thing in its own
                        style, and what it is unlocking is spelled out in the
                        dialog rather than crammed onto the button. */}
                    <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-800">
                        <Lock className="h-3 w-3" />
                        Sound-by-sound tips
                    </p>
                    <Button size="sm" onClick={() => setUpgradeOpen(true)}
                            className="rounded-full text-xs h-7 px-3 mt-2">
                        Unlock
                    </Button>
                </div>
                <UpgradeDialog
                    open={upgradeOpen}
                    onOpenChange={setUpgradeOpen}
                    title="Phoneme-level coaching tips"
                    message="Free doesn't include the sound-by-sound breakdown of what you just said."
                />
            </div>
        );
    }
    // Each card is tinted by its WORD score, which is the number that tinted
    // the same word up in the phrase. It used to use the worst single sound
    // inside the word instead, so a word sitting amber at 55 got a red card
    // because one phoneme in it scored 20, and the two halves of the same
    // screen disagreed about how bad the word was.
    const paddingCls = dense ? 'text-xs px-3 py-2' : 'text-sm px-3 py-2.5';
    const cardClsFor = (score) => `${paddingCls} rounded-lg border text-stone-700 ${tipCardTint(score)}`;
    const scoreCls = cardScoreText;
    // One card per word, not one card per source — a word with both a
    // Speechace phone tip and a Gemini note used to render as two separate
    // cards that looked like two different apps talking. Order follows the
    // word's position in the phrase (when given) so the list reads
    // left-to-right like the phrase itself, instead of Speechace's tips
    // bunched first and Gemini's tacked on at the end.
    const byWord = new Map();
    const order = [];
    const addTo = (key, field, item) => {
        if (!byWord.has(key)) { byWord.set(key, { phone: [], stress: [], issues: [] }); order.push(key); }
        byWord.get(key)[field].push(item);
    };
    const repeats = countWords(phrase);
    // One card per ATTEMPT at a word: "le" said twice is two cards, labelled.
    //
    // Never more attempts than the phrase has of that word. The coaching once
    // numbered three notes on a single "travaille" as 1, 2 and 3, and the extra
    // two became cards for attempts that never happened, with no score on them.
    // Without a phrase to count (the session summary) they are left as sent.
    const attemptKey = (word) => {
        const most = repeats.get(normWordKey(word));
        return (t) => occKey(word, most ? Math.min(occOf(t), most) : occOf(t));
    };
    for (const t of phoneTips) addTo(attemptKey(t.word)(t), 'phone', t);
    for (const t of stressTips) addTo(attemptKey(t.word)(t), 'stress', t);
    for (const it of issues) addTo(attemptKey(it.word)(it), 'issues', it);
    if (phrase) {
        const phraseOrder = [];
        const occSoFar = new Map();
        for (const raw of phrase.split(/\s+/)) {
            const k = normWordKey(raw);
            if (!k) continue;
            const occ = (occSoFar.get(k) || 0) + 1;
            occSoFar.set(k, occ);
            phraseOrder.push(`${k}#${occ}`);
        }
        // Words the phrase mentions, in phrase order, first; anything left
        // over (a word key that didn't match, e.g. missing/odd data) still
        // shows, just at the end rather than getting dropped.
        const rest = order.filter(k => !phraseOrder.includes(k));
        order.length = 0;
        order.push(...phraseOrder.filter(k => byWord.has(k)), ...rest);
    }

    // Which words survive the cap, decided by SCORE. Phrase order alone meant
    // the six shown were simply the first six, so on a long phrase the word
    // that scored worst could be cut for one that scored 60.
    //
    // Chosen worst-first, then put back into phrase order, so the list still
    // reads left to right like the phrase it belongs to.
    // The same capped list that decides which words are highlighted in the
    // phrase (lib/score feedbackKeys), so the cards and the colours are always
    // the same words. Tapping a word outside it still shows its feedback.
    const kept = feedbackKeys({ phoneTips, stressTips, issues, wordScores });
    // The number each card shows: the word's own score, else the one carried
    // on its tips (the session summary), else the average of its weak sounds.
    const cardScore = (key) => {
        const { phone } = byWord.get(key);
        const own = (phone.find(t => t.word_score != null) || {}).word_score;
        if (wordScoreByKey.has(key)) return Math.round(wordScoreByKey.get(key));
        if (own != null) return Math.round(own);
        return phone.length ? Math.round(phone.reduce((s, t) => s + t.score, 0) / phone.length) : null;
    };
    let shown = order.filter(k => kept.has(k));
    if (hideGood) {
        const weak = shown.filter(k => cardScore(k) == null || needsWork(cardScore(k)));
        if (weak.length) shown = weak;
    }
    if (worstFirst) {
        shown = [...shown].sort((a, b) => (cardScore(a) ?? 101) - (cardScore(b) ?? 101));
    }

    return (
        <div className="text-left" data-testid="pronunciation-tips">
            {title && <p className={`font-semibold text-stone-700 mb-2 ${dense ? 'text-xs' : 'text-sm'}`}>{title}</p>}
            {/* The cap lives here now, not in the payload. The backend used to
                send only the six worst sounds in the whole clip, which kept
                this list short but also meant a word could be highlighted in
                the phrase above with its explanation already discarded. The
                full set is sent; this list shows the worst handful of it. */}
            <ul className="space-y-1.5">
                {shown.map((word, i) => {
                    const { phone, stress, issues: wordIssues } = byWord.get(word);
                    const [baseKey, occ] = word.split('#');
                    const displayWord = phone[0]?.word || stress[0]?.word || wordIssues[0]?.word
                        || byWord.get(word).word || baseKey;
                    // "le (2nd)" only when the phrase has it more than once.
                    const cardLabel = repeats.get(baseKey) > 1
                        ? `${displayWord} (${ordinal(Number(occ) || 1)})` : displayWord;
                    // The word's own score, the one the phrase above is tinted
                    // by. Only when it is missing does the card fall back to
                    // the average of the sounds inside it.
                    const wordScore = cardScore(word);
                    const cardSyllables = (phone.find(t => (t.syllables || []).length > 1) || {}).syllables
                        || syllablesFor(wordScores, displayWord, Number(occ) || 1);
                    return (
                        <li key={i} className={cardClsFor(wordScore)}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-1.5">
                                    <CardWord>{cardLabel}</CardWord>
                                    {/* The model word only. Their own voice for it
                                        is off for now, as in the phrase above. */}
                                    <WordPlayButton word={displayWord} />
                                </div>
                                {wordScore != null && (
                                    <span className={`text-sm font-bold ${scoreCls(wordScore)} tabular-nums`}>
                                        {wordScore}%
                                    </span>
                                )}
                            </div>
                            {/* The syllables come straight under the word (Leanne,
                                16 Oct 2026), from the tips or, failing that, from
                                the word's own score entry. */}
                            {cardSyllables.length > 1 && (
                                <p className="text-sm leading-snug text-stone-600"><SyllableList syllables={cardSyllables} /></p>
                            )}
                            {/* What that score means, in Speechace's own words. */}
                            {wordScoreDescription(wordScore, cardSoundCount(phone)) && (
                                <p className="text-sm leading-snug text-stone-500">{wordScoreDescription(wordScore, cardSoundCount(phone))}</p>
                            )}
                            {/* Nothing singled out inside this word: say so,
                                in the same words as the tapped-word bar,
                                rather than leaving the card blank or, worse,
                                leaving the word without a card at all. */}
                            {phone.length || stress.length || wordIssues.length
                                ? <WordTipLines phone={phone} stress={stress} issues={wordIssues}
                                                coached={coached} hideSyllables showLesson={showLessons} />
                                : <p className="mt-1 text-sm leading-snug text-stone-600">{NO_SOUND_LINE}</p>}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
